import { ColumnDef } from '@tanstack/react-table';
import { DotIcon } from 'lucide-react';
import { memo } from 'react';

import { PullState, PullStateIcon } from '@/components/ui/pull-state-icon';
import { ThumbsDownIcon } from '@/components/ui/thumbs-down-icon';
import { ThumbsUpIcon } from '@/components/ui/thumbs-up-icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PullSummaryWithRepo } from '@/features/pulls/services/pulls';
import { cn, formatNumber } from '@/lib/utils';

export const HeaderIcons = memo((col: ColumnDef<PullSummaryWithRepo, unknown>) => {
  const meta = col.meta;
  if (!meta?.icon) return col.header?.toString();

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger>{<meta.icon className="size-4" />}</TooltipTrigger>
      <TooltipContent>{col.header?.toString()}</TooltipContent>
    </Tooltip>
  );
});

const tooltipClass = cn(
  'relative before:z-10 before:content-[attr(data-tooltip)] before:absolute before:px-2 before:py-1 before:-translate-x-1/2 before:left-1/2 before:bottom-full before:mb-1.5 before:text-xs',
  'before:whitespace-nowrap before:rounded-md before:bg-black/75 before:text-white before:opacity-0 hover:before:opacity-100 before:transition-opacity before:pointer-events-none'
);

export const ReviewStatusCell = memo(
  ({ reviewDecision }: { reviewDecision?: 'CHANGES_REQUESTED' | 'APPROVED' | 'REVIEW_REQUIRED' | null }) => {
    switch (reviewDecision) {
      case 'REVIEW_REQUIRED':
        return (
          <span data-tooltip="Review required" className={tooltipClass}>
            <DotIcon className="size-4 text-yellow-500" />
          </span>
        );
      case 'APPROVED':
        return (
          <span data-tooltip="Changes approved" className={tooltipClass}>
            <ThumbsUpIcon className="size-4 text-emerald-500" />
          </span>
        );
      case 'CHANGES_REQUESTED':
        return (
          <span data-tooltip="Changes requested" className={tooltipClass}>
            <ThumbsDownIcon className="size-4 text-red-500" />
          </span>
        );
      default:
        return (
          <span data-tooltip="No review" className={tooltipClass}>
            -
          </span>
        );
    }
  }
);

export const ChangesCell = memo(({ deletions, additions }: { deletions?: number; additions?: number }) => {
  const hasAdditions = !!(additions && additions > 0);
  const hasDeletions = !!(deletions && deletions > 0);

  return (
    <div className="w-full flex items-center gap-1">
      <div className="flex-1 flex items-center justify-end">
        {hasAdditions && <span className="text-xs text-emerald-500">+{formatNumber(additions)} </span>}
      </div>
      <div className="px-1">
        <span
          className={cn(
            'invisible text-normal font-light text-muted-foreground',
            hasAdditions && hasDeletions && 'visible'
          )}
        >
          |
        </span>
      </div>
      <div className="flex-1 flex items-center justify-start">
        {hasDeletions && <span className="text-xs text-red-600">-{formatNumber(deletions)} </span>}
      </div>
    </div>
  );
});

export const CommentsCell = memo(
  ({
    comments,
    reviewThreads,
  }: {
    comments: number;
    reviewThreads?: ({ comments: { totalCount: number } } | null)[] | null;
  }) => {
    const reviewCommentCount =
      reviewThreads?.reduce((acc, thread) => acc + (thread?.comments?.totalCount ?? 0), 0) ?? 0;
    return <p className="text-xs">{comments + reviewCommentCount}</p>;
  }
);

export const stateDisplayName = (state: 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT') => {
  switch (state) {
    case 'OPEN':
      return 'Open';
    case 'CLOSED':
      return 'Closed';
    case 'MERGED':
      return 'Merged';
    case 'DRAFT':
      return 'Draft';
  }
};

export const StateCell = memo(
  ({ state, size = 15, tooltipEnabled = true }: { state: PullState; size?: number; tooltipEnabled?: boolean }) => {
    switch (state) {
      case 'OPEN':
        return (
          <span data-tooltip="Open" className={cn(tooltipEnabled && tooltipClass)}>
            <PullStateIcon state={state} width={size} height={size} />
          </span>
        );
      case 'CLOSED':
        return (
          <span data-tooltip="Closed" className={cn(tooltipEnabled && tooltipClass)}>
            <PullStateIcon state={state} width={size} height={size} />
          </span>
        );
      case 'MERGED':
        return (
          <span data-tooltip="Merged" className={cn(tooltipEnabled && tooltipClass)}>
            <PullStateIcon state={state} width={size} height={size} />
          </span>
        );
      case 'DRAFT':
        return (
          <span data-tooltip="Draft" className={cn(tooltipEnabled && tooltipClass)}>
            <PullStateIcon state={state} width={size} height={size} />
          </span>
        );
      default:
        return null;
    }
  }
);

export const TitleCell = memo(({ title }: { title: string }) => {
  return <span className="font-md text-sm truncate leading-none">{title}</span>;
});

export const AuthorCell = memo(({ avatarUrl, login }: { avatarUrl?: string; login?: string }) => {
  if (!avatarUrl || !login) return null;

  return <img src={avatarUrl} alt={login} className="size-6 rounded-full" />;
});
